import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Invoice Management - Admin Dashboard
 *
 * View, manage, download, and email invoices
 * Auto-generated from approved memos
 */
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { InvoicePDFDocument } from '../../components/InvoicePDFDocument';
import { apiClient } from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import mockDataService from '../../services/mockDataService';
import styled from 'styled-components';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_COLORS } from '../../types/invoice';
import { FaDownload, FaEnvelope, FaCheck, FaTimes, FaFileInvoiceDollar, FaSync, FaDollarSign, FaCheckCircle, FaClock, FaPlus } from 'react-icons/fa';
import { CreateInvoiceModal } from '../../components/CreateInvoiceModal';
import TabPageLayout from './FinancialHub/TabPageLayout';
import { Button, StatsGrid, StatCard, StatIcon, StatLabel, StatValue, TableContainer, Table, Th, Td, Tr, FiltersContainer, FilterGroup, Label, Input, Select, EmptyState, EmptyStateIcon, EmptyStateText, SortableTh } from './FinancialHub/SharedComponents';
// Status Badge
const StatusBadge = styled.span `
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => INVOICE_STATUS_COLORS[props.status]}15;
  color: ${props => INVOICE_STATUS_COLORS[props.status]};
  border: 1px solid ${props => INVOICE_STATUS_COLORS[props.status]}40;
`;
// Action buttons
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => {
    switch (props.variant) {
        case 'primary':
            return `
          background: rgba(184, 134, 11, 0.1);
          border-color: #B8860B;
          color: #B8860B;
          &:hover:not(:disabled) { background: rgba(184, 134, 11, 0.2); }
        `;
        case 'success':
            return `
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
          color: #10b981;
          &:hover:not(:disabled) { background: rgba(16, 185, 129, 0.2); }
        `;
        case 'danger':
            return `
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
          &:hover:not(:disabled) { background: rgba(239, 68, 68, 0.2); }
        `;
        default:
            return `
          background: var(--bg-tertiary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover:not(:disabled) { background: var(--bg-secondary); }
        `;
    }
}}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const InvoiceNumber = styled.span `
  font-family: monospace;
  font-weight: 600;
  color: var(--text-primary);
  background: var(--bg-tertiary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
`;
const CustomerInfo = styled.div `
  .customer-name {
    font-weight: 500;
    color: var(--text-primary);
  }
  .customer-email {
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
`;
const InvoiceManagement = () => {
    const authStore = useAuthStore();
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const isFetchingRef = useRef(false);
    // Sort state
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    // Create modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const fetchInvoices = async () => {
        if (isFetchingRef.current)
            return;
        isFetchingRef.current = true;
        try {
            setIsLoading(true);
            if (mockDataService.isMockMode()) {
                const mockInvoices = mockDataService.getInvoices();
                const filtered = statusFilter !== 'all'
                    ? mockInvoices.filter(inv => inv.status === statusFilter)
                    : mockInvoices;
                const stats = {
                    total: filtered.length,
                    byStatus: {
                        draft: filtered.filter(i => i.status === 'draft').length,
                        issued: filtered.filter(i => i.status === 'issued').length,
                        paid: filtered.filter(i => i.status === 'paid').length,
                        cancelled: filtered.filter(i => i.status === 'cancelled').length,
                        overdue: filtered.filter(i => i.status === 'overdue').length,
                    },
                    totalValue: filtered.reduce((sum, inv) => sum + inv.total, 0),
                    totalPaid: filtered.reduce((sum, inv) => sum + (inv.amountPaid || 0), 0),
                    totalOutstanding: filtered.reduce((sum, inv) => sum + (inv.amountDue || inv.total), 0),
                };
                setInvoices(filtered);
                setFilteredInvoices(filtered);
                setStatistics(stats);
            }
            else {
                const queryParams = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
                const response = await apiClient.get(`invoices-get${queryParams}`);
                if (response.ok && response.data?.success && response.data.invoices) {
                    setInvoices(response.data.invoices);
                    setFilteredInvoices(response.data.invoices);
                    setStatistics(response.data.statistics);
                }
                else {
                    notify.error('Error', response.data?.error || 'Failed to load invoices');
                    setInvoices([]);
                    setFilteredInvoices([]);
                }
            }
        }
        catch (error) {
            console.error('Unexpected error:', error);
            notify.error('Error', 'Failed to load invoices');
            setInvoices([]);
            setFilteredInvoices([]);
        }
        finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };
    useEffect(() => {
        if (authStore.isAuthenticated && !authStore.isLoading) {
            fetchInvoices();
        }
    }, [authStore.isAuthenticated, authStore.isLoading, statusFilter]);
    // Handle sort
    const handleSort = (key) => {
        if (sortKey === key) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortKey(null);
            }
            else {
                setSortDirection('asc');
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    // Sort invoices
    const sortInvoices = (invoicesToSort) => {
        if (!sortKey || !sortDirection)
            return invoicesToSort;
        return [...invoicesToSort].sort((a, b) => {
            let aValue;
            let bValue;
            switch (sortKey) {
                case 'invoiceNumber':
                    aValue = a.invoiceNumber?.toLowerCase() || '';
                    bValue = b.invoiceNumber?.toLowerCase() || '';
                    break;
                case 'customer':
                    aValue = (a.customerBusinessName || a.customerName)?.toLowerCase() || '';
                    bValue = (b.customerBusinessName || b.customerName)?.toLowerCase() || '';
                    break;
                case 'issueDate':
                    aValue = new Date(a.issueDate).getTime();
                    bValue = new Date(b.issueDate).getTime();
                    break;
                case 'dueDate':
                    aValue = new Date(a.dueDate).getTime();
                    bValue = new Date(b.dueDate).getTime();
                    break;
                case 'items':
                    aValue = a.lineItems?.length || 0;
                    bValue = b.lineItems?.length || 0;
                    break;
                case 'total':
                    aValue = a.total || 0;
                    bValue = b.total || 0;
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                default:
                    return 0;
            }
            if (aValue < bValue)
                return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue)
                return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };
    // Filter and sort invoices
    useEffect(() => {
        let filtered = invoices;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = invoices.filter(invoice => invoice.invoiceNumber.toLowerCase().includes(term) ||
                invoice.customerName.toLowerCase().includes(term) ||
                invoice.customerBusinessName?.toLowerCase().includes(term) ||
                invoice.customerEmail.toLowerCase().includes(term));
        }
        // Apply sorting
        filtered = sortInvoices(filtered);
        setFilteredInvoices(filtered);
    }, [searchTerm, invoices, sortKey, sortDirection]);
    const handleDownloadInvoice = async (invoice) => {
        try {
            setDownloadingInvoiceId(invoice.id);
            const blob = await pdf(_jsx(InvoicePDFDocument, { invoice: invoice })).toBlob();
            const filename = `${invoice.invoiceNumber}_${invoice.customerName.replace(/\s+/g, '_')}.pdf`;
            saveAs(blob, filename);
            notify.success('PDF Downloaded', `Invoice ${invoice.invoiceNumber} downloaded`);
        }
        catch (error) {
            console.error('Error generating invoice PDF:', error);
            notify.error('Download Failed', 'Failed to generate invoice PDF');
        }
        finally {
            setDownloadingInvoiceId(null);
        }
    };
    const handleMarkAsPaid = async (invoice) => {
        try {
            if (mockDataService.isMockMode()) {
                mockDataService.updateInvoice(invoice.id, { status: 'paid' });
                await fetchInvoices();
                notify.success('Invoice Paid', `Invoice ${invoice.invoiceNumber} marked as paid`);
            }
            else {
                const response = await apiClient.patch(`invoices-update-status/${invoice.id}`, { status: 'paid' });
                if (response.ok && response.data?.success) {
                    await fetchInvoices();
                    notify.success('Invoice Paid', `Invoice ${invoice.invoiceNumber} marked as paid`);
                }
                else {
                    notify.error('Update Failed', response.error || 'Failed to update invoice status');
                }
            }
        }
        catch (error) {
            console.error('Failed to mark invoice as paid:', error);
            notify.error('Update Failed', 'Failed to update invoice status');
        }
    };
    const handleCancelInvoice = async (invoice) => {
        if (!confirm(`Cancel invoice ${invoice.invoiceNumber}?`))
            return;
        try {
            if (mockDataService.isMockMode()) {
                mockDataService.updateInvoice(invoice.id, { status: 'cancelled' });
                await fetchInvoices();
                notify.success('Invoice Cancelled', `Invoice ${invoice.invoiceNumber} cancelled`);
            }
            else {
                const response = await apiClient.patch(`invoices-update-status/${invoice.id}`, { status: 'cancelled' });
                if (response.ok && response.data?.success) {
                    await fetchInvoices();
                    notify.success('Invoice Cancelled', `Invoice ${invoice.invoiceNumber} cancelled`);
                }
                else {
                    notify.error('Update Failed', response.error || 'Failed to cancel invoice');
                }
            }
        }
        catch (error) {
            console.error('Failed to cancel invoice:', error);
            notify.error('Update Failed', 'Failed to cancel invoice');
        }
    };
    const handleEmailInvoice = (invoice) => {
        notify.info('Coming Soon', 'Email delivery will be available in a future update.');
    };
    const formatCurrency = (amount) => `$${amount.toLocaleString()}`;
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
    // Stats section
    const renderStats = () => (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { color: "#3b82f6", children: [_jsx(StatIcon, { color: "#3b82f6", children: _jsx(FaFileInvoiceDollar, {}) }), _jsx(StatLabel, { children: "Total Invoices" }), _jsx(StatValue, { children: statistics?.total || 0 })] }), _jsxs(StatCard, { color: "#8b5cf6", children: [_jsx(StatIcon, { color: "#8b5cf6", children: _jsx(FaDollarSign, {}) }), _jsx(StatLabel, { children: "Total Value" }), _jsx(StatValue, { children: formatCurrency(statistics?.totalValue || 0) })] }), _jsxs(StatCard, { color: "#10b981", children: [_jsx(StatIcon, { color: "#10b981", children: _jsx(FaCheckCircle, {}) }), _jsx(StatLabel, { children: "Paid" }), _jsx(StatValue, { children: formatCurrency(statistics?.totalPaid || 0) })] }), _jsxs(StatCard, { color: "#f59e0b", children: [_jsx(StatIcon, { color: "#f59e0b", children: _jsx(FaClock, {}) }), _jsx(StatLabel, { children: "Outstanding" }), _jsx(StatValue, { children: formatCurrency(statistics?.totalOutstanding || 0) })] })] }));
    // Actions section
    const renderActions = () => (_jsxs("div", { style: { display: 'flex', gap: '0.75rem' }, children: [_jsxs(Button, { "$primary": true, onClick: () => setShowCreateModal(true), children: [_jsx(FaPlus, { style: { marginRight: '0.5rem' } }), " Create Invoice"] }), _jsxs(Button, { onClick: fetchInvoices, disabled: isLoading, children: [_jsx(FaSync, { style: { marginRight: '0.5rem' } }), " ", isLoading ? 'Loading...' : 'Refresh'] })] }));
    // Filters section
    const renderFilters = () => (_jsxs(FiltersContainer, { children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Search" }), _jsx(Input, { type: "text", placeholder: "Search by invoice #, customer...", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Statuses" }), _jsx("option", { value: "issued", children: "Issued" }), _jsx("option", { value: "paid", children: "Paid" }), _jsx("option", { value: "overdue", children: "Overdue" }), _jsx("option", { value: "cancelled", children: "Cancelled" }), _jsx("option", { value: "draft", children: "Draft" })] })] })] }));
    // Table
    const renderTable = () => {
        if (filteredInvoices.length === 0) {
            return (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, { children: _jsx(FaFileInvoiceDollar, {}) }), _jsx(EmptyStateText, { children: searchTerm || statusFilter !== 'all'
                            ? 'No invoices match your search criteria'
                            : 'No invoices found. Invoices are created when memos are approved.' })] }));
        }
        return (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(SortableTh, { sortKey: "invoiceNumber", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Invoice #" }), _jsx(SortableTh, { sortKey: "customer", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Customer" }), _jsx(SortableTh, { sortKey: "issueDate", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Issue Date" }), _jsx(SortableTh, { sortKey: "dueDate", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Due Date" }), _jsx(SortableTh, { sortKey: "items", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Items" }), _jsx(SortableTh, { sortKey: "total", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Total" }), _jsx(SortableTh, { sortKey: "status", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: filteredInvoices.map((invoice) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsx(InvoiceNumber, { children: invoice.invoiceNumber }) }), _jsx(Td, { children: _jsxs(CustomerInfo, { children: [_jsx("div", { className: "customer-name", children: invoice.customerBusinessName || invoice.customerName }), _jsx("div", { className: "customer-email", children: invoice.customerEmail })] }) }), _jsx(Td, { children: formatDate(invoice.issueDate) }), _jsx(Td, { children: formatDate(invoice.dueDate) }), _jsxs(Td, { children: [invoice.lineItems.length, " item", invoice.lineItems.length !== 1 ? 's' : ''] }), _jsx(Td, { children: _jsx(PriceCell, { children: formatCurrency(invoice.total) }) }), _jsx(Td, { children: _jsx(StatusBadge, { status: invoice.status, children: INVOICE_STATUS_LABELS[invoice.status] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { variant: "primary", onClick: () => handleDownloadInvoice(invoice), disabled: downloadingInvoiceId === invoice.id, title: "Download PDF", children: _jsx(FaDownload, {}) }), _jsx(ActionButton, { onClick: () => handleEmailInvoice(invoice), title: "Email Invoice", children: _jsx(FaEnvelope, {}) }), (invoice.status === 'issued' || invoice.status === 'overdue') && (_jsx(ActionButton, { variant: "success", onClick: () => handleMarkAsPaid(invoice), title: "Mark as Paid", children: _jsx(FaCheck, {}) })), invoice.status !== 'paid' && invoice.status !== 'cancelled' && (_jsx(ActionButton, { variant: "danger", onClick: () => handleCancelInvoice(invoice), title: "Cancel Invoice", children: _jsx(FaTimes, {}) }))] }) })] }, invoice.id))) })] }) }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(TabPageLayout, { title: "Invoice Management", subtitle: "View, manage, download, and email invoices", stats: renderStats(), actions: renderActions(), filters: renderFilters(), children: isLoading ? (_jsx("div", { style: { padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }, children: "Loading invoices..." })) : (renderTable()) }), _jsx(CreateInvoiceModal, { isOpen: showCreateModal, onClose: () => setShowCreateModal(false), onSuccess: () => {
                    setShowCreateModal(false);
                    fetchInvoices();
                } })] }));
};
export default InvoiceManagement;
